import { useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Card,
  CardBody,
  SimpleGrid,
  Image,
  Text,
  Badge,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  VStack,
  useColorModeValue,
  Icon,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  useToast,
  useDisclosure,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  PopoverHeader,
  PopoverCloseButton,
  Select,
  FormControl,
  FormLabel,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Portal,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEllipsisV,
  faEdit,
  faTrash,
  faExternalLinkAlt,
  faChartBar,
  faCopy,
  faFilter,
  faImage,
  faCode,
  faFilm,
  faTimes,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import { useAds, useAdMutations } from '../hooks/useAds';
import { useAdvertiser } from '../hooks/useAdvertisers';
import { formatPercent } from '../utils/format';
import AdModal from '../components/ads/AdModal';
import AdPreviewModal from '../components/ads/AdPreviewModal';
import EmailSnippetModal from '../components/ads/EmailSnippetModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import { useLicenseProtection } from '../hooks/useLicenseProtection';
import { formatFullNumber } from '../utils/format';
import DateRangePicker from '../components/common/DateRangePicker';
import { useRecoilState } from 'recoil';
import { dateRangeAtom } from '../state/dateRangeAtom';
import { parseDateRange } from '../hooks/useAnalytics';

function getTypeIcon(type) {
  switch (type) {
    case 'image':
      return faImage;
    case 'html':
      return faCode;
    case 'gif':
      return faFilm;
    default:
      return faImage;
  }
}

function getTypeColor(type) {
  switch (type) {
    case 'image':
      return 'blue';
    case 'html':
      return 'purple';
    case 'gif':
      return 'orange';
    default:
      return 'gray';
  }
}

function AdCard({ ad, onViewStats, onEdit, onDuplicate, onPreview, onEmailSnippet, onDelete, onCampaignClick, isReadOnly }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const imageBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <Card
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      _hover={{ boxShadow: 'xl', borderColor: 'purple.300' }}
      transition="all 0.2s ease"
    >
      {/* Preview Image */}
      <Box
        position="relative"
        bg={imageBg}
        minH="120px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        cursor="pointer"
        onClick={() => onPreview(ad)}
        borderTopRadius="xl"
      >
        <Image
          src={ad.preview}
          alt={ad.name}
          maxH="150px"
          objectFit="contain"
          fallback={
            <Box textAlign="center" py={8}>
              <Icon boxSize={10} color="gray.400">
                <FontAwesomeIcon icon={faImage} />
              </Icon>
            </Box>
          }
        />
        {/* Type Badge Overlay */}
        <Badge
          position="absolute"
          top={3}
          left={3}
          colorScheme={getTypeColor(ad.type)}
          borderRadius="full"
          px={2}
          py={1}
          fontSize="xs"
          textTransform="uppercase"
        >
          <HStack spacing={1}>
            <Icon boxSize={3}>
              <FontAwesomeIcon icon={getTypeIcon(ad.type)} />
            </Icon>
            <Text>{ad.type}</Text>
          </HStack>
        </Badge>
        {/* Status Indicator */}
        <Box
          position="absolute"
          top={3}
          right={3}
          w="10px"
          h="10px"
          borderRadius="full"
          bg={ad.status === 'active' ? 'green.400' : 'yellow.400'}
          boxShadow={`0 0 0 3px ${ad.status === 'active' ? 'rgba(72, 187, 120, 0.3)' : 'rgba(236, 201, 75, 0.3)'}`}
        />
      </Box>

      <CardBody>
        {/* Header */}
        <Flex justify="space-between" align="start" mb={3}>
          <VStack align="start" spacing={1} flex={1}>
            <Text fontWeight="600" noOfLines={1} fontSize="md">
              {ad.name}
            </Text>
            <HStack spacing={1} flexWrap="wrap">
              {(ad.campaigns || []).slice(0, 2).map(c => (
                <Badge
                  key={c.campaign_id}
                  colorScheme="purple"
                  borderRadius="full"
                  fontSize="xs"
                  cursor="pointer"
                  _hover={{ opacity: 0.8 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCampaignClick && onCampaignClick({ ...ad, campaign_id: c.campaign_id });
                  }}
                >
                  {c.campaign_name}
                </Badge>
              ))}
              {(ad.campaigns || []).length > 2 && (
                <Badge colorScheme="gray" borderRadius="full" fontSize="xs">
                  +{ad.campaigns.length - 2} more
                </Badge>
              )}
            </HStack>
          </VStack>
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<FontAwesomeIcon icon={faEllipsisV} />}
              variant="ghost"
              size="sm"
              borderRadius="lg"
            />
            <Portal>
              <MenuList zIndex={9999} py={2} borderRadius="xl">
                <MenuItem icon={<FontAwesomeIcon icon={faChartBar} />} onClick={() => onViewStats(ad)} mx={2} borderRadius="lg" w="calc(100% - 16px)">
                  View Stats
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faEdit} />}
                  onClick={() => onEdit(ad)}
                  isDisabled={isReadOnly}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Edit
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faCopy} />}
                  onClick={() => onDuplicate(ad)}
                  isDisabled={isReadOnly}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Duplicate
                </MenuItem>
                <MenuItem icon={<FontAwesomeIcon icon={faExternalLinkAlt} />} onClick={() => onPreview(ad)} mx={2} borderRadius="lg" w="calc(100% - 16px)">
                  Preview
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faEnvelope} />}
                  onClick={() => onEmailSnippet(ad)}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Email Snippet
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  color={isReadOnly ? 'gray.400' : 'red.500'}
                  onClick={() => onDelete(ad)}
                  isDisabled={isReadOnly}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Flex>

        {/* Size Badge */}
        <HStack spacing={2} mb={4}>
          <Badge variant="outline" borderRadius="full" fontSize="xs">
            {ad.size}
          </Badge>
          <Badge
            colorScheme={ad.status === 'active' ? 'green' : 'yellow'}
            borderRadius="full"
            fontSize="xs"
          >
            {ad.status}
          </Badge>
        </HStack>

        {/* Stats */}
        <SimpleGrid columns={3} spacing={2}>
          <Box
            textAlign="center"
            p={2}
            bg={statBg}
            borderRadius="lg"
            transition="all 0.2s"
            _hover={{ transform: 'scale(1.02)' }}
          >
            <Text color={textSecondary} fontSize="xs" fontWeight="500" mb={1}>
              Impr.
            </Text>
            <Text fontWeight="700" fontSize="sm" bgGradient="linear(to-r, brand.500, accent.500)" bgClip="text">
              {formatFullNumber(ad.impressions)}
            </Text>
          </Box>
          <Box
            textAlign="center"
            p={2}
            bg={statBg}
            borderRadius="lg"
            transition="all 0.2s"
            _hover={{ transform: 'scale(1.02)' }}
          >
            <Text color={textSecondary} fontSize="xs" fontWeight="500" mb={1}>
              Clicks
            </Text>
            <Text fontWeight="700" fontSize="sm" bgGradient="linear(to-r, cyan.500, blue.500)" bgClip="text">
              {formatFullNumber(ad.clicks)}
            </Text>
          </Box>
          <Box
            textAlign="center"
            p={2}
            bg={statBg}
            borderRadius="lg"
            transition="all 0.2s"
            _hover={{ transform: 'scale(1.02)' }}
          >
            <Text color={textSecondary} fontSize="xs" fontWeight="500" mb={1}>
              CTR
            </Text>
            <Text fontWeight="700" fontSize="sm" bgGradient="linear(to-r, green.500, teal.500)" bgClip="text">
              {formatPercent(ad.ctr, 2)}
            </Text>
          </Box>
        </SimpleGrid>
      </CardBody>
    </Card>
  );
}

function AdsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignIdFilter = searchParams.get('campaign');
  const advertiserFilter = searchParams.get('advertiser');

  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  const toast = useToast();
  const { isReadOnly, protectAction } = useLicenseProtection();

  // Modal states
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isEmailSnippetOpen, onOpen: onEmailSnippetOpen, onClose: onEmailSnippetClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();

  const [selectedAd, setSelectedAd] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    campaign: '',
    status: '',
    type: '',
    size: '',
  });

  // Date range state (shared globally across all tabs)
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);

  const handleDateRangeChange = useCallback((range) => {
    setDateRange(range);
  }, [setDateRange]);

  // Build API filters with date range
  const apiFilters = useMemo(() => {
    const dates = parseDateRange(dateRange);
    return { start: dates.start.toISOString(), end: dates.end.toISOString() };
  }, [dateRange]);

  // Fetch ads from API
  const { data: ads, loading, error, refetch } = useAds(apiFilters);
  const { createAd, updateAd, deleteAd, duplicateAd } = useAdMutations();

  // Fetch advertiser data for filter display
  const { data: advertiserData } = useAdvertiser(advertiserFilter);

  // Helper to generate preview image based on ad type
  const generatePreview = (ad, serverUrl) => {
    const width = ad.width || 300;
    const height = ad.height || 250;
    const adType = ad.ad_type || ad.type || 'image';

    // For video ads, generate a thumbnail with play button (video files can't be used as image src)
    if (adType === 'video') {
      return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><defs><linearGradient id="vbg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:%231a1a2e"/><stop offset="100%" style="stop-color:%2316213e"/></linearGradient></defs><rect width="${width}" height="${height}" fill="url(%23vbg)"/><circle cx="${width/2}" cy="${height/2 - 20}" r="30" fill="rgba(102,126,234,0.9)"/><polygon points="${width/2 - 10},${height/2 - 35} ${width/2 - 10},${height/2 - 5} ${width/2 + 15},${height/2 - 20}" fill="white"/><text x="${width/2}" y="${height/2 + 30}" text-anchor="middle" fill="white" font-family="Arial" font-size="14" font-weight="bold">Video Ad</text><text x="${width/2}" y="${height/2 + 50}" text-anchor="middle" fill="rgba(255,255,255,0.7)" font-family="Arial" font-size="11">${width}x${height}</text></svg>`)}`;
    }

    // For image/gif/text/html with file_path, use it
    if (ad.file_path) {
      if (ad.file_path.startsWith('http://') || ad.file_path.startsWith('https://')) {
        return ad.file_path;
      }
      return `${serverUrl}/uploads/ads/${ad.file_path}`;
    }

    // Fallback placeholder
    return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="${width}" height="${height}" fill="%236366f1"/><text x="${width/2}" y="${height/2 + 5}" text-anchor="middle" fill="white" font-family="Arial" font-size="14">${adType.toUpperCase()} ${width}x${height}</text></svg>`)}`;
  };

  // Helper to format ad data from API
  const serverUrl = window.location.origin.replace(':3058', ':3059');
  const formatAd = (a) => ({
    id: a.ad_id || a.id,
    ad_id: a.ad_id || a.id,
    ad_name: a.ad_name || a.name,
    name: a.ad_name || a.name || 'Untitled Ad',
    campaign: a.campaigns?.[0]?.campaign_name || a.campaign_name || 'Unknown Campaign',
    campaign_id: a.campaigns?.[0]?.campaign_id || a.campaign_id,
    campaign_name: a.campaigns?.[0]?.campaign_name || a.campaign_name,
    campaigns: a.campaigns || [{ campaign_id: a.campaign_id, campaign_name: a.campaign_name }],
    advertiser_id: a.advertiser_id,
    advertiser_name: a.advertiser_name,
    type: a.ad_type || a.type || 'image',
    ad_type: a.ad_type || a.type || 'image',
    size: a.size || `${a.width || 300}x${a.height || 250}`,
    width: a.width || 300,
    height: a.height || 250,
    status: a.status || 'active',
    impressions: a.total_impressions || a.impressions || 0,
    total_impressions: a.total_impressions || a.impressions || 0,
    clicks: a.total_clicks || a.clicks || 0,
    total_clicks: a.total_clicks || a.clicks || 0,
    ctr: a.ctr || (a.total_impressions > 0 ? ((a.total_clicks || 0) / a.total_impressions * 100) : 0),
    preview: generatePreview(a, serverUrl),
    file_path: a.file_path,
    file_size: a.file_size,
    destination_url: a.destination_url,
    alt_text: a.alt_text,
    html_content: a.html_content,
    weight: a.weight,
    created_at: a.created_at,
  });

  // Memoize formatted ads to prevent unnecessary re-renders
  const allAds = useMemo(() => {
    return ads ? ads.map(formatAd) : [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, serverUrl]);

  // Extract unique filter options from ads
  const filterOptions = useMemo(() => {
    const campaignNames = new Set();
    allAds.forEach(ad => {
      (ad.campaigns || []).forEach(c => { if (c.campaign_name) campaignNames.add(c.campaign_name); });
    });
    const campaigns = [...campaignNames].sort();
    const statuses = [...new Set(allAds.map(ad => ad.status).filter(Boolean))];
    const types = [...new Set(allAds.map(ad => ad.type).filter(Boolean))];
    const sizes = [...new Set(allAds.map(ad => ad.size).filter(Boolean))].sort();
    return { campaigns, statuses, types, sizes };
  }, [allAds]);

  // Apply filters to ads list (URL filters take precedence)
  const adsList = useMemo(() => {
    return allAds.filter(ad => {
      // URL-based advertiser filter (from navigation)
      if (advertiserFilter && String(ad.advertiser_id) !== advertiserFilter) return false;
      // URL-based campaign ID filter (from navigation) — check if any campaign matches
      if (campaignIdFilter && !(ad.campaigns || []).some(c => String(c.campaign_id) === campaignIdFilter)) return false;
      // Local dropdown campaign filter — check if any campaign name matches
      if (!campaignIdFilter && filters.campaign && !(ad.campaigns || []).some(c => c.campaign_name === filters.campaign)) return false;
      if (filters.status && ad.status !== filters.status) return false;
      if (filters.type && ad.type !== filters.type) return false;
      if (filters.size && ad.size !== filters.size) return false;
      return true;
    });
  }, [allAds, filters, campaignIdFilter, advertiserFilter]);

  // Get campaign name for URL filter display
  const urlFilterCampaignName = useMemo(() => {
    if (!campaignIdFilter || !allAds.length) return null;
    for (const ad of allAds) {
      const match = (ad.campaigns || []).find(c => String(c.campaign_id) === campaignIdFilter);
      if (match) return match.campaign_name;
    }
    return null;
  }, [allAds, campaignIdFilter]);

  // Clear URL filters
  const clearUrlFilter = (key) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    setSearchParams(newParams);
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v).length;

  // Clear a single filter
  const clearFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({ campaign: '', status: '', type: '', size: '' });
  };

  // Handlers
  const handleViewStats = (ad) => {
    setSelectedAd(ad);
    onPreviewOpen(); // Use preview modal for stats too
  };

  const handleEdit = (ad) => {
    setSelectedAd(ad);
    onEditOpen();
  };

  const handleDuplicate = async (ad) => {
    toast({
      title: 'Duplicating ad...',
      status: 'info',
      duration: 2000,
    });
    try {
      await duplicateAd(ad.campaigns?.[0]?.campaign_id || ad.campaign_id, ad.ad_id || ad.id);
      toast({
        title: 'Ad duplicated',
        status: 'success',
        duration: 3000,
      });
      refetch();
    } catch (err) {
      toast({
        title: 'Error duplicating ad',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handlePreview = (ad) => {
    setSelectedAd(ad);
    onPreviewOpen();
  };

  const handleEmailSnippet = (ad) => {
    setSelectedAd(ad);
    onEmailSnippetOpen();
  };

  const handleCampaignClick = (ad) => {
    navigate(`/campaigns?campaign=${ad.campaign_id}`);
  };

  const handleDeleteClick = (ad) => {
    setSelectedAd(ad);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAd) return;
    setIsSubmitting(true);
    try {
      await deleteAd(selectedAd.campaign_id, selectedAd.ad_id);
      toast({
        title: 'Ad deleted',
        status: 'success',
        duration: 3000,
      });
      onDeleteClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error deleting ad',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubmit = async (campaignId, formData) => {
    setIsSubmitting(true);
    try {
      await createAd(campaignId, formData);
      toast({
        title: 'Ad uploaded',
        status: 'success',
        duration: 3000,
      });
      onCreateClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error uploading ad',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (campaignId, formData, adId) => {
    if (!selectedAd) return;
    setIsSubmitting(true);
    try {
      await updateAd(campaignId, adId || selectedAd.ad_id, formData);
      toast({
        title: 'Ad updated',
        status: 'success',
        duration: 3000,
      });
      onEditClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error updating ad',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={{ base: 6, md: 8 }}
        gap={4}
      >
        <Box>
          <Heading
            size={{ base: 'lg', md: 'xl' }}
            bgGradient="linear(to-r, brand.500, accent.500)"
            bgClip="text"
            fontWeight="700"
          >
            Ads
          </Heading>
          <Text color={textSecondary} mt={1}>
            Manage your ad creatives and assets
          </Text>
        </Box>
        <HStack spacing={3} flexWrap="wrap">
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            showCompare={false}
            size="md"
          />
          <Popover
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            placement="bottom-end"
          >
            <PopoverTrigger>
              <Button
                leftIcon={<FontAwesomeIcon icon={faFilter} />}
                variant="outline"
                size={{ base: 'sm', md: 'md' }}
                borderRadius="xl"
                onClick={onFilterOpen}
                position="relative"
              >
                Filter
                {activeFilterCount > 0 && (
                  <Badge
                    colorScheme="brand"
                    borderRadius="full"
                    ml={2}
                    fontSize="xs"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              bg={cardBg}
              borderColor={borderColor}
              borderRadius="xl"
              w="300px"
              boxShadow="lg"
            >
              <PopoverHeader fontWeight="600" borderColor={borderColor}>
                Filter Ads
              </PopoverHeader>
              <PopoverCloseButton />
              <PopoverBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm">Campaign</FormLabel>
                    <Select
                      placeholder="All campaigns"
                      value={filters.campaign}
                      onChange={(e) => setFilters(prev => ({ ...prev, campaign: e.target.value }))}
                      bg={inputBg}
                      borderRadius="lg"
                      size="sm"
                    >
                      {filterOptions.campaigns.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Status</FormLabel>
                    <Select
                      placeholder="All statuses"
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      bg={inputBg}
                      borderRadius="lg"
                      size="sm"
                    >
                      {filterOptions.statuses.map(s => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Type</FormLabel>
                    <Select
                      placeholder="All types"
                      value={filters.type}
                      onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                      bg={inputBg}
                      borderRadius="lg"
                      size="sm"
                    >
                      {filterOptions.types.map(t => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Size</FormLabel>
                    <Select
                      placeholder="All sizes"
                      value={filters.size}
                      onChange={(e) => setFilters(prev => ({ ...prev, size: e.target.value }))}
                      bg={inputBg}
                      borderRadius="lg"
                      size="sm"
                    >
                      {filterOptions.sizes.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </Select>
                  </FormControl>
                </VStack>
              </PopoverBody>
              {activeFilterCount > 0 && (
                <PopoverFooter borderColor={borderColor}>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={clearAllFilters}
                    leftIcon={<FontAwesomeIcon icon={faTimes} />}
                  >
                    Clear all filters
                  </Button>
                </PopoverFooter>
              )}
            </PopoverContent>
          </Popover>
          <Button
            leftIcon={<FontAwesomeIcon icon={faPlus} />}
            colorScheme="brand"
            size={{ base: 'sm', md: 'md' }}
            borderRadius="xl"
            onClick={() => protectAction(onCreateOpen)}
            isDisabled={isReadOnly}
            title={isReadOnly ? 'License required to upload ads' : undefined}
          >
            Upload Ad
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      {(() => {
        const totals = adsList.reduce(
          (acc, ad) => ({
            impressions: acc.impressions + (ad.impressions || 0),
            clicks: acc.clicks + (ad.clicks || 0),
          }),
          { impressions: 0, clicks: 0 }
        );
        const avgCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
        return (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, pink.400, pink.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Ads</Text>
                  <Text fontSize="2xl" fontWeight="bold">{adsList.length}</Text>
                </VStack>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, purple.400, purple.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Impressions</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatFullNumber(totals.impressions)}</Text>
                </VStack>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, green.400, green.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Clicks</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatFullNumber(totals.clicks)}</Text>
                </VStack>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, orange.400, orange.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Avg CTR</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatPercent(avgCtr, 2, true)}</Text>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        );
      })()}

      {/* Active Filter Tags */}
      {(activeFilterCount > 0 || campaignIdFilter || advertiserFilter) && (
        <Wrap spacing={2} mb={4}>
          {/* URL-based advertiser filter (from navigation) */}
          {advertiserFilter && (
            <WrapItem>
              <Tag size="lg" borderRadius="full" colorScheme="purple" variant="subtle">
                <TagLabel>Advertiser: {advertiserData?.advertiser_name || `#${advertiserFilter}`}</TagLabel>
                <TagCloseButton onClick={() => clearUrlFilter('advertiser')} />
              </Tag>
            </WrapItem>
          )}
          {/* URL-based campaign filter (from navigation) */}
          {campaignIdFilter && urlFilterCampaignName && (
            <WrapItem>
              <Tag size="lg" borderRadius="full" colorScheme="blue" variant="subtle">
                <TagLabel>Campaign: {urlFilterCampaignName}</TagLabel>
                <TagCloseButton onClick={() => clearUrlFilter('campaign')} />
              </Tag>
            </WrapItem>
          )}
          {/* Local dropdown campaign filter */}
          {!campaignIdFilter && filters.campaign && (
            <WrapItem>
              <Tag size="md" borderRadius="full" colorScheme="brand" variant="subtle">
                <TagLabel>Campaign: {filters.campaign}</TagLabel>
                <TagCloseButton onClick={() => clearFilter('campaign')} />
              </Tag>
            </WrapItem>
          )}
          {filters.status && (
            <WrapItem>
              <Tag size="md" borderRadius="full" colorScheme="green" variant="subtle">
                <TagLabel>Status: {filters.status}</TagLabel>
                <TagCloseButton onClick={() => clearFilter('status')} />
              </Tag>
            </WrapItem>
          )}
          {filters.type && (
            <WrapItem>
              <Tag size="md" borderRadius="full" colorScheme="purple" variant="subtle">
                <TagLabel>Type: {filters.type}</TagLabel>
                <TagCloseButton onClick={() => clearFilter('type')} />
              </Tag>
            </WrapItem>
          )}
          {filters.size && (
            <WrapItem>
              <Tag size="md" borderRadius="full" colorScheme="blue" variant="subtle">
                <TagLabel>Size: {filters.size}</TagLabel>
                <TagCloseButton onClick={() => clearFilter('size')} />
              </Tag>
            </WrapItem>
          )}
        </Wrap>
      )}

      {/* Error State */}
      {error && (
        <Alert status="error" borderRadius="xl" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Ads Grid */}
      {loading || ads === null ? (
        <Center py={20}>
          <Spinner size="xl" color="purple.500" thickness="3px" />
        </Center>
      ) : adsList.length > 0 ? (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={{ base: 4, md: 6 }}>
          {adsList.map((ad) => (
            <AdCard
              key={ad.id}
              ad={ad}
              onViewStats={handleViewStats}
              onEdit={(a) => protectAction(() => handleEdit(a))}
              onDuplicate={(a) => protectAction(() => handleDuplicate(a))}
              onPreview={handlePreview}
              onEmailSnippet={handleEmailSnippet}
              onDelete={(a) => protectAction(() => handleDeleteClick(a))}
              onCampaignClick={handleCampaignClick}
              isReadOnly={isReadOnly}
            />
          ))}
        </SimpleGrid>
      ) : (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <Text color={textSecondary} textAlign="center" py={8}>
              {advertiserFilter
                ? 'No ads found for this advertiser.'
                : campaignIdFilter
                  ? 'No ads found for this campaign.'
                  : activeFilterCount > 0
                    ? 'No ads match your filters.'
                    : 'No ads found. Upload your first ad to get started.'}
            </Text>
          </CardBody>
        </Card>
      )}

      {/* Modals */}
      <AdModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSubmit={handleCreateSubmit}
        isLoading={isSubmitting}
      />

      <AdModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        onSubmit={handleEditSubmit}
        ad={selectedAd}
        isLoading={isSubmitting}
      />

      <AdPreviewModal
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        ad={selectedAd}
      />

      <EmailSnippetModal
        isOpen={isEmailSnippetOpen}
        onClose={onEmailSnippetClose}
        ad={selectedAd}
      />

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Ad"
        itemName={selectedAd?.name}
        isLoading={isSubmitting}
      />
    </Box>
  );
}

export default AdsPage;
